import { TuiAnimated } from '@taiga-ui/cdk';
import { TuiFloatingContainer, TuiSlides } from '@taiga-ui/layout';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { getDropZoneAcceptValidator } from '../drop-zone-accept-validator';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { DepartmentPickerComponent } from '@modules/department/components/department-picker/department-picker.component';
import { WOUNDTRACK_PREFIXES } from '@modules/forms/form-modules/woundtrack/config/woundtrack-export.config';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { SelectLabelComponent } from '@modules/label/select-label/select-label.component';
import { PatientEditComponent } from '@modules/patient/patient-edit/patient-edit/patient-edit.component';
import { PaywallAlertComponent } from '@modules/paywall/paywall-alert/paywall-alert.component';
import { SadmAnalysisInputComponent } from '@modules/sadm/components/sadm-analysis-input/sadm-analysis-input.component';
import { ShowIfStorageSpaceAvailableDirective } from '@modules/shared/directives/show-if-storage-space-available.directive';
import { ImageInputComponent } from '@modules/shared/image-input/image-input.component';
import { Store } from '@ngrx/store';
import { Label, Patient } from '@pixacare/domain';
import {
  appendPatientToCreateSequenceFormData,
  appendSadmEntityToCreateSequenceFormData,
  FlattenedSequence,
} from '@pixacare/dtos';
import { FormAnswer } from '@pixacare/forms';
import { appendToFormData, formatToIso } from '@pixacare/shared';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { MAX_MEDIA_UPLOAD_SIZE } from '@shared/utils/max-media-upload-size';
import { RequestResult } from '@shared/request-state/models/request-result';
import {
  TuiButton,
  TuiCalendar,
  TuiDialogContext,
  TuiError,
  TuiInput,
  TuiLabel,
  TuiLoader,
  TuiNotification,
  TuiNotificationService,
} from '@taiga-ui/core';
import { TuiButtonLoading, TuiInputDateTime, TuiNotificationMiddle, TuiStepper, TuiTextarea } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { map, Observable, ReplaySubject } from 'rxjs';
import { SequenceCreateStep } from 'src/app/shared/models/enums/sequence-create-step.enum';
import { AutoCompleteLabelModel } from 'src/app/shared/models/helpers/auto-complete-label-model';
import { isLabel } from 'src/app/shared/models/helpers/label-helper';
import { selectSadmClients } from 'src/app/shared/store/sadm/sadm.selectors';
import { selectIsLongVideoEnabled } from 'src/app/shared/store/clients/clients.selectors';
import { environment } from 'src/environments/environment';
import { SadmEntityService } from '../../sadm/services/sadm-entity.service';
import { isImagePayloadTooLarge } from '../services/is-image-payload-too-large';
import { SequenceCreationService } from '../services/sequence-creation.service';
import { SequenceCreateInputs } from './sequence-create-inputs';
import { SlidesScrollResetDirective } from '@modules/shared/directives/slides-scroll-reset.directive';

type FooterButton = {
  label: string;
  isDisabled: boolean;
  isLoading: boolean;
  action: () => void;
  testId?: string;
};

@Component({
  templateUrl: './sequence-create.component.html',
  providers: [SadmEntityService, SequenceCreationService],
  host: { class: 'flex flex-1 flex-col' },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SlidesScrollResetDirective,
    TranslocoDirective,
    AsyncPipe,
    TuiFloatingContainer,
    TuiSlides,
    PaywallAlertComponent,
    ShowIfStorageSpaceAvailableDirective,
    TuiStepper,
    FormsModule,
    ReactiveFormsModule,
    TuiInput,
    TuiLoader,
    TuiAnimated,
    TuiButtonLoading,
    TuiTextarea,
    TuiInputDateTime,
    TuiLabel,
    TuiError,
    DepartmentPickerComponent,
    TuiNotification,
    TuiNotificationMiddle,
    TuiButton,
    PatientEditComponent,
    ImageInputComponent,
    SadmAnalysisInputComponent,
    SelectLabelComponent,
    TuiCalendar,
  ],
})
export class SequenceCreateComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly translocoService = inject(TranslocoService);
  private readonly alertService = inject<TuiNotificationService>(TuiNotificationService);
  private readonly sequenceCreationService = inject(SequenceCreationService);
  private readonly context = inject<TuiDialogContext<boolean, SequenceCreateInputs>>(POLYMORPHEUS_CONTEXT);

  clientCode: string = this.context.data.clientCode;
  patient?: Patient = this.context.data.patient;
  departmentIds: number[] = this.context.data.departmentIds ?? [];
  telemonitoringId?: number = this.context.data.telemonitoringId;
  sadmEntityId?: number = this.context.data.sadmEntityId;
  sadmEntityDraft?: SadmEntityDraft = this.context.data.sadmEntityDraft;
  analysisFormAnswer?: FormAnswer = this.context.data.analysisFormAnswer;
  protocolFormAnswer?: FormAnswer = this.context.data.protocolFormAnswer;
  step?: SequenceCreateStep = this.context.data.step ?? SequenceCreateStep.INFORMATION;
  sequenceMedias: File[] = this.context.data.sequenceMedias ?? [];

  isPatientEditFormValid$ = new ReplaySubject<boolean>(1);
  private readonly isPatientEditFormValid = toSignal(this.isPatientEditFormValid$);
  SequenceCreateStep = SequenceCreateStep;
  slideDirection = 0;
  private currentPageStep = this.step;

  get pageStep(): SequenceCreateStep {
    return this.currentPageStep;
  }

  set pageStep(step: SequenceCreateStep) {
    this.slideDirection = step - this.currentPageStep;
    this.currentPageStep = step;
  }
  errors: string[] = [];
  readonly isCreationOngoing = signal(false);

  // General
  sequenceDataDate: Date = new Date();

  // Patient
  selectedPatient: Patient;

  // Medias

  // Analysis
  displayAnalysis$: Observable<boolean>;
  selectedSadmEntityId: number = this.sadmEntityId;
  createdAnalysisFormAnswer = this.analysisFormAnswer;
  createdProtocolFormAnswer = this.protocolFormAnswer;

  // Labels
  selectedLabels: AutoCompleteLabelModel[] = [];
  description: string;

  // Device
  deviceType = 'WEB';
  protected readonly isLongVideoEnabled = toSignal(this.store.select(selectIsLongVideoEnabled(this.clientCode)), {
    initialValue: false,
  });
  readonly dropZoneAcceptValidator = computed(() => getDropZoneAcceptValidator(this.isLongVideoEnabled()));
  dropZoneMaxFileSize = MAX_MEDIA_UPLOAD_SIZE;

  commonClientCode = environment.commonClientCode;

  get hasTooLargeImagePayload(): boolean {
    return isImagePayloadTooLarge(this.sequenceMedias);
  }

  get hasPatient(): boolean {
    return !!(
      this.selectedPatient?.businessIdentifier ||
      this.selectedPatient?.firstName ||
      this.selectedPatient?.lastName ||
      this.selectedPatient?.birthDate
    );
  }

  get isSecondStepDisabled(): boolean {
    return !this.telemonitoringId && this.departmentIds.length === 0 && this.clientCode !== this.commonClientCode;
  }

  get isThirdStepDisabled(): boolean {
    return this.isPatientEditFormValid() === false;
  }

  protected get stepFooter(): { previousButton: FooterButton; nextButton: FooterButton } {
    switch (this.pageStep) {
      case SequenceCreateStep.INFORMATION:
        return {
          previousButton: {
            label: marker('Annuler'),
            isDisabled: false,
            isLoading: false,
            action: () => this.cancel(),
          },
          nextButton: {
            label: marker('Suivant'),
            isDisabled: this.isSecondStepDisabled,
            isLoading: false,
            action: () => this.next(),
            testId: 'next-page',
          },
        };
      case SequenceCreateStep.PATIENT:
        return {
          previousButton: {
            label: marker('Précédent'),
            isDisabled: false,
            isLoading: false,
            action: () => this.previous(),
          },
          nextButton: {
            label: this.hasPatient ? marker('Suivant') : marker('Ignorer'),
            isDisabled: this.isSecondStepDisabled || this.isThirdStepDisabled,
            isLoading: false,
            action: () => this.next(),
            testId: 'next-page',
          },
        };
      case SequenceCreateStep.PICTURES:
        return {
          previousButton: {
            label: marker('Précédent'),
            isDisabled: this.isCreationOngoing(),
            isLoading: false,
            action: () => this.previous(),
          },
          nextButton: {
            label: marker('Ajouter'),
            isDisabled: this.hasTooLargeImagePayload,
            isLoading: this.isCreationOngoing(),
            action: () => this.create(),
            testId: 'create-sequence',
          },
        };
    }
  }

  get formInputs(): ModuleResolverInputs {
    return {
      files: [...this.sequenceMedias],
    };
  }

  ngOnInit(): void {
    if (this.patient) {
      this.selectedPatient = this.patient;
    }

    this.displayAnalysis$ = this.store
      .select(selectSadmClients)
      .pipe(map((sadmClients) => !!this.analysisFormAnswer || Object.values(sadmClients).length > 0));
  }

  validateForm(): boolean {
    this.errors = [];

    if (this.sequenceMedias.length === 0) {
      this.errors.push(
        this.translocoService.translate(
          this.isLongVideoEnabled()
            ? 'Veuillez ajouter au moins une photo ou une vidéo'
            : 'Veuillez ajouter au moins une photo.',
        ),
      );
    }

    if (this.hasTooLargeImagePayload) {
      this.errors.push(this.translocoService.translate('La taille totale des photos ne peut pas dépasser 1 Go.'));
    }

    this.errors.forEach((error) => {
      this.alertService
        .open(error, {
          label: this.translocoService.translate('Vérifiez les informations'),
          appearance: 'negative',
        })
        .subscribe();
    });

    return this.errors.length === 0;
  }

  previous(): void {
    this.pageStep--;
  }

  next(): void {
    switch (this.pageStep) {
      case SequenceCreateStep.INFORMATION:
        this.pageStep = this.patient ? SequenceCreateStep.PICTURES : SequenceCreateStep.PATIENT;
        break;
      case SequenceCreateStep.PATIENT:
        this.pageStep = SequenceCreateStep.PICTURES;
        break;
    }
  }

  cancel(): void {
    this.context.completeWith(false);
  }

  generateFormData(): FormData {
    const data = new FormData();

    const naturalLabels = this.selectedLabels
      .filter((selectedLabel) => isLabel(selectedLabel.value))
      .map((selectedLabel) => (selectedLabel.value as Label).id.toString());

    const createdLabels = this.selectedLabels
      .filter((selectedLabel) => !isLabel(selectedLabel.value))
      .map((selectedLabel) => selectedLabel.value.toString());

    appendToFormData(data, {
      clientCode: this.clientCode,
      createdOn: formatToIso(this.sequenceDataDate),
      naturalLabels,
      createdLabels,
      telemonitoringId: this.telemonitoringId,
      description: this.description,
      deviceType: this.deviceType,
    });

    // Departments
    if (!this.telemonitoringId) {
      appendToFormData(data, {
        departmentIds: this.departmentIds,
      });
    }

    // Patient
    if (this.selectedPatient) {
      appendPatientToCreateSequenceFormData(data, this.selectedPatient);
    }

    // Analysis
    if ((this.selectedSadmEntityId || this.sadmEntityDraft) && this.createdAnalysisFormAnswer) {
      if (this.selectedSadmEntityId) {
        appendToFormData(data, {
          sadmEntityId: this.selectedSadmEntityId,
        });
      }
      if (this.sadmEntityDraft) {
        appendSadmEntityToCreateSequenceFormData(data, this.sadmEntityDraft);
      }

      appendToFormData(data, {
        formAnswer: JSON.stringify(this.createdAnalysisFormAnswer),
        ...(this.createdProtocolFormAnswer && {
          protocolCreationFormAnswer: JSON.stringify(this.createdProtocolFormAnswer),
        }),
      });
    }

    return data;
  }

  create(): void {
    if (this.isCreationOngoing() || !this.validateForm()) {
      return;
    }

    this.sequenceCreationService
      .createSequence(this.clientCode, this.generateFormData(), this.sequenceMedias)
      .subscribe((result) => this.handleCreationResult(result));
  }

  private handleCreationResult(result: RequestResult<FlattenedSequence>): void {
    this.isCreationOngoing.set(result.isLoading);

    if (result.isSuccess) {
      this.context.completeWith(true);
    }

    if (result.hasError) {
      this.alertService
        .open(this.translocoService.translate('Impossible de synchroniser la séquence.'), {
          label: this.translocoService.translate('Une erreur est survenue.'),
          appearance: 'negative',
        })
        .subscribe();
    }
  }

  onOutputPicturesChange(files: File[]): void {
    if (!files?.length) {
      return;
    }

    const previousFiles = this.sequenceMedias.filter(
      (file) => !WOUNDTRACK_PREFIXES.some((prefix) => file.name.startsWith(prefix)),
    );
    this.sequenceMedias = [...previousFiles, ...files];
  }
}
